import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import toastr from 'toastr';
import postal from 'postal';
import classNames from 'classnames';
import breakpoints from '../../shared/breakpoints';
import Callout from '../../shared/callout';
import ConfirmationModal from '../shared/confirmation-modal';
import defaultOnErrorHandler from '../../shared/default-on-error-handler';
import ErrorBoundary from '../../shared/error-boundary';
import deleteWireBySlug from '../../services/wire/delete-service';

const MenuItem = ({
  href, children, itemClass, ...other
}) => (
  <li className="text-left">
    <a
      href={href}
      className={classNames(itemClass, { '--current': window.location.pathname === href })}
      {...other}
    >
      {children}
    </a>
  </li>
);

MenuItem.propTypes = {
  href: PropTypes.string,
  children: PropTypes.node.isRequired,
  itemClass: PropTypes.string.isRequired,
};

MenuItem.defaultProps = {
  href: '#',
};

export default class EditorsManageWireCallout extends React.Component {
  static propTypes = {
    wire: PropTypes.shape({
      wire_id: PropTypes.number.isRequired,
      title: PropTypes.string,
      published_at: PropTypes.string,
      wire: PropTypes.shape({
        slug: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]).isRequired,
        user_id: PropTypes.number.isRequired,
      }).isRequired,
      editors_picked: PropTypes.bool,
      featured_count: PropTypes.number,
    }).isRequired,
    rightAligned: PropTypes.bool,
  };

  static defaultProps = {
    rightAligned: true,
  };

  constructor(props, context) {
    super(props, context);

    this.state = {
      presented: false,
    };

    [
      'editorPick', 'editorAssignCategory', 'confirmDelete', 'deleteWire',
    ].forEach(m => (this[m] = this[m].bind(this)));
  }

  componentDidMount() {
    $(document).on('mouseup.editorsManageWireCallout', e => {
      if (breakpoints.getScreenSize() === 'small') return;

      const $target = $(e.target);
      const wireId = this.props.wire.wire_id.toString();

      if ($target.parents('.callout-inner-body').length) return;
      if ($target.hasClass('editors-manage-wire-callout') && $target.children('span').attr('data-wire-id') === wireId) return;
      if ($target.parents('.editors-manage-wire-callout').length && $target.attr('data-wire-id') === wireId) return;

      this.dismissCallout();
    });
  }

  componentWillUnmount() {
    $(document).off('mouseup.editorsManageWireCallout');
  }

  getPermissions() {
    const { published_at: publishedAt } = this.props.wire;
    const { user_id: userId } = this.props.wire.wire;
    const { profile } = window.Livewire.currentUser;
    const isOwner = window.Livewire.currentUser.id === userId;
    const isPublished = !!publishedAt;

    // Can edit if: editor can modify others' wires, OR owner can edit
    // (drafts always, published if permitted)
    const canEditWire = profile.canModifyWiresOfOthers
      || (isOwner && (!isPublished || profile.canEditPublishedWires));

    // Can update category if: published AND (editor OR owner with category assignment permission)
    const canUpdateCategory = isPublished
      && (profile.canModifyWiresOfOthers || (isOwner && profile.canAssignWiresCategories));

    return {
      profile, isOwner, isPublished, canEditWire, canUpdateCategory,
    };
  }

  editorPick(event) {
    event && event.preventDefault();

    postal.publish({
      channel: 'commands.editors-picks',
      topic: 'pick-wire',
      data: {
        wire: this.props.wire,
        onDismiss: this.dismissCallout(),
      },
    });
  }

  toggleCallout() {
    this.setState(state => ({
      presented: !state.presented,
    }));
  }

  dismissCallout() {
    this.setState({
      presented: false,
    });
  }

  editorAssignCategory(event) {
    event && event.preventDefault();

    postal.publish({
      channel: 'commands.categories',
      topic: 'assign-wire-category',
      data: {
        wire: this.props.wire,
        onDismiss: this.dismissCallout(),
      },
    });
  }

  confirmDelete(e) {
    e && e.preventDefault();
    this.confirmModal.present();
  }

  deleteWire() {
    const { slug } = this.props.wire.wire;
    toastr.info('Please wait, your wire is being deleted...');

    deleteWireBySlug(slug)
      .then(() => {
        this.dismissCallout();
        toastr.clear();
        toastr.success('Your wire has been deleted');
        setTimeout(() => {
          if (_.includes(window.location.pathname, `/wires/${slug}`)) {
            window.location = '/';
          } else {
            window.location.reload();
          }
        }, 3000);
      })
      .catch(e => {
        defaultOnErrorHandler(e, 'Failed to delete wire');
      });
  }

  renderMenuItems() {
    const { editors_picked: editorsPicked } = this.props.wire;
    const { slug } = this.props.wire.wire;
    const {
      profile, isOwner, isPublished, canEditWire, canUpdateCategory,
    } = this.getPermissions();
    const menuText = editorsPicked ? "Manage Editor's Picks" : "Add To Editor's Picks";

    return (
      <ul>
        {isPublished && profile.canManageEditorsPicks && (
          <MenuItem itemClass="editors-picks" href="#" onClick={this.editorPick}>{ menuText }</MenuItem>
        )}
        {canEditWire && <MenuItem itemClass="edit-wire" href={`/wires/${slug}/edit`}>Edit Wire</MenuItem>}
        {canUpdateCategory && <MenuItem itemClass="update-category" onClick={this.editorAssignCategory}>Update Category</MenuItem>}
        {!isPublished && <MenuItem itemClass="preview-wire" href={`/wires/${slug}`}>Preview Wire</MenuItem>}
        {isOwner && <MenuItem itemClass="delete-wire" href="#" onClick={this.confirmDelete}>Delete Wire</MenuItem>}
      </ul>
    );
  }

  render() {
    // offsetX:
    //  medium size callout width(200px)
    //    - callout body left padding(16px)
    //    - indicator width, after 45 deg rotation(28.28px)
    //    - half width of the ellipipsis stroke width (3px)

    const modalProps = {
      title: 'Are you sure you want to delete this wire?',
      message: this.props.wire.featured_count
        ? 'This wire is featured on your profile. If you delete this wire it will be removed from your Livewire account entirely and cannot be recovered.'
        : 'Deleting this wire will permanently remove it from your Livewire account and you will not be able to recover it. Would you still like to delete this wire?',
      submitText: 'Delete',
      extraClass: '--large',
      onConfirm: this.deleteWire,
    };

    return (
      <ErrorBoundary>
        <Callout
          indicatorPosition={this.props.rightAligned ? 'top-left' : 'top-right'}
          offsetY={60}
          offsetX={this.props.rightAligned ? 153 : 10}
          size="medium"
          calloutTrigger={<span className="icon-lw icon-lw-ellipsis" data-wire-id={this.props.wire.wire_id} />}
          show={this.state.presented}
          togglePresent={() => this.toggleCallout()}
          classModifier="editors-manage-wire-callout --wire"
        >
          <div className="callout-inner-body">
            <section>
              <h3 className="text-left">Manage Wire</h3>
              {this.renderMenuItems()}
            </section>
          </div>
        </Callout>
        <ConfirmationModal {...modalProps} ref={el => (this.confirmModal = el)} />
      </ErrorBoundary>
    );
  }
}
